import { useFavoriteScopedBusesContext } from "contexts/FavoriteBusesContext/FavoriteScopedBusesContext";
import useMapViewData from "contexts/MapViewDataContext/hooks/useMapViewData";
import useSelectedBuses from "contexts/SelectedBusesContext/useSelectedBuses";
import { createContext, PropsWithChildren } from "react";
import { ScopedBus } from "types/busType";
import { SelectedBusesAtLocation } from "types/selectedBusesAtLocation";

const SelectedScopedBusContext = createContext<ScopedBus | undefined>(
    undefined
);

const SelectedScopedBusProvider: React.FC<PropsWithChildren<unknown>> = ({
    children
}: PropsWithChildren<unknown>) => {
    const { selectedBusesAtLocation } = useSelectedBuses();
    const { scopedBuses } = useMapViewData();
    const favoriteScopedBuses = useFavoriteScopedBusesContext();

    const maybeSelectedScopedBus = maybeGetSelectedScopedBus(
        selectedBusesAtLocation,
        scopedBuses,
        favoriteScopedBuses
    );

    if (maybeSelectedScopedBus) {
        return (
            <SelectedScopedBusContext.Provider value={maybeSelectedScopedBus}>
                {children}
            </SelectedScopedBusContext.Provider>
        );
    } else {
        return null;
    }
};

const maybeGetSelectedScopedBus = (
    maybeSelectedBuses: SelectedBusesAtLocation | null,
    scopedBuses: ReadonlyArray<ScopedBus>,
    favoriteScopedBuses: ReadonlyArray<ScopedBus>
): ScopedBus | null | undefined => {
    if (maybeSelectedBuses === null) {
        return null;
    } else {
        const busId = maybeSelectedBuses.selectedBusId;
        // The favorites panel lists buses the map query filters out, so fall
        // back to the by-id fetch when the selection is not in the map data.
        return (
            scopedBuses.find((scopedBus) => scopedBus.bus.id === busId) ??
            favoriteScopedBuses.find((scopedBus) => scopedBus.bus.id === busId)
        );
    }
};

export { SelectedScopedBusProvider, SelectedScopedBusContext };
